'use client'

import { useState } from 'react'

interface Props {
  data: {
    year: number
    months: (number | null)[] // 12 entries, null if no data
  }[]
  yearlyReturns?: Map<number, number> | { year: number; return: number }[]
}

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const MONTH_SHORT = ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D']

function getCellColor(value: number | null): string {
  if (value === null) return 'rgba(39,39,42,0.5)'
  if (value >= 30) return 'rgba(34,197,94,0.55)'
  if (value >= 15) return 'rgba(34,197,94,0.40)'
  if (value >= 5) return 'rgba(34,197,94,0.25)'
  if (value >= 0) return 'rgba(34,197,94,0.12)'
  if (value >= -5) return 'rgba(239,68,68,0.12)'
  if (value >= -15) return 'rgba(239,68,68,0.25)'
  if (value >= -30) return 'rgba(239,68,68,0.40)'
  return 'rgba(239,68,68,0.55)'
}

function getTextColor(value: number | null): string {
  if (value === null) return '#71717a'
  if (value >= 30) return '#ffffff'
  if (value >= 5) return '#4ade80'
  if (value >= 0) return '#86efac'
  if (value >= -5) return '#fca5a5'
  if (value <= -30) return '#ffffff'
  return '#f87171'
}

function fmt(val: number | null): string {
  if (val === null) return '–'
  return `${val >= 0 ? '+' : ''}${val.toFixed(0)}%`
}

export function MonthlyHeatmap({ data, yearlyReturns }: Props) {
  const [hover, setHover] = useState<{ row: number; col: number } | null>(null)

  // Build yearly returns lookup
  const yrMap = new Map<number, number>()
  if (yearlyReturns) {
    if (Array.isArray(yearlyReturns)) {
      yearlyReturns.forEach((yr) => yrMap.set(yr.year, yr.return))
    }
  }

  // Compute monthly averages
  const monthAvgs = Array.from({ length: 12 }, (_, mi) => {
    const vals = data.map((d) => d.months[mi]).filter((v): v is number => v !== null)
    if (vals.length === 0) return null
    return vals.reduce((a, b) => a + b, 0) / vals.length
  })

  // Total row count: data rows + avg row
  const totalRows = data.length + 1
  // Total cols: year label + 12 months + yearly total = 14
  const totalCols = 14

  // Determine if a cell should be dimmed, highlighted, etc.
  const getCellState = (row: number, col: number) => {
    if (!hover) return ''
    if (row === hover.row && col === hover.col) return 'highlight-cell'
    if (row === hover.row) return 'highlight-row'
    if (col === hover.col) return 'highlight-col'
    return 'dimmed'
  }

  const opacityForState = (state: string) => {
    if (state === 'dimmed') return 0.3
    if (state === 'highlight-row' || state === 'highlight-col') return 0.7
    return 1
  }

  // Hover detail info
  const hoverDetail = (() => {
    if (!hover) return null
    const { row, col } = hover
    if (col === 0 || col === 13) return null // year label or total col
    const monthIdx = col - 1
    if (row < data.length) {
      const val = data[row].months[monthIdx]
      if (val === null) return null
      return { label: `${MONTH_LABELS[monthIdx]} ${data[row].year}`, value: val }
    }
    // Avg row
    const val = monthAvgs[monthIdx]
    if (val === null) return null
    return { label: `${MONTH_LABELS[monthIdx]} avg`, value: val }
  })()

  return (
    <div>
      {/* ═══ DESKTOP HEATMAP (sm+) ═══ */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '2px' }}>
          <thead>
            <tr>
              <th className="text-left text-zinc-400 text-[10px] font-medium w-12 pl-1" />
              {MONTH_LABELS.map((m, i) => (
                <th key={m} className="text-center text-zinc-400 text-[10px] font-medium uppercase tracking-wider py-1.5">
                  {m}
                </th>
              ))}
              <th
                className="text-center text-zinc-400 text-[10px] font-medium uppercase tracking-wider py-1.5 w-14"
                style={{ borderLeft: '2px solid #27272a' }}
              >
                Year
              </th>
            </tr>
          </thead>
          <tbody>
            {/* Data rows */}
            {data.map((row, ri) => {
              const yr = yrMap.get(row.year)
              return (
                <tr key={row.year}>
                  <td className="text-zinc-400 text-xs tabular-nums font-medium pl-1 pr-2">
                    {row.year}
                  </td>
                  {row.months.map((val, mi) => {
                    const col = mi + 1
                    const state = getCellState(ri, col)
                    return (
                      <td
                        key={mi}
                        className="text-center py-2 cursor-default transition-opacity"
                        style={{
                          backgroundColor: getCellColor(val),
                          opacity: opacityForState(state),
                          boxShadow: state === 'highlight-cell' ? 'inset 0 0 0 1px rgba(255,255,255,0.25)' : undefined,
                        }}
                        onMouseEnter={() => setHover({ row: ri, col })}
                        onMouseLeave={() => setHover(null)}
                      >
                        <span
                          className="text-xs tabular-nums font-semibold"
                          style={{ color: getTextColor(val) }}
                        >
                          {fmt(val)}
                        </span>
                      </td>
                    )
                  })}
                  <td
                    className="text-center py-2"
                    style={{ borderLeft: '2px solid #27272a' }}
                  >
                    {yr !== undefined ? (
                      <span
                        className={`text-xs tabular-nums font-bold ${
                          yr >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}
                      >
                        {fmt(yr)}
                      </span>
                    ) : null}
                  </td>
                </tr>
              )
            })}

            {/* Averages row */}
            <tr>
              <td
                className="text-zinc-400 text-[10px] font-medium uppercase tracking-wider pl-1 pr-2"
                style={{ borderTop: '2px solid #27272a' }}
              >
                Avg
              </td>
              {monthAvgs.map((val, mi) => {
                const col = mi + 1
                const state = getCellState(data.length, col)
                return (
                  <td
                    key={mi}
                    className="text-center py-2 cursor-default transition-opacity"
                    style={{
                      backgroundColor: getCellColor(val),
                      borderTop: '2px solid #27272a',
                      opacity: opacityForState(state),
                      boxShadow: state === 'highlight-cell' ? 'inset 0 0 0 1px rgba(255,255,255,0.25)' : undefined,
                    }}
                    onMouseEnter={() => setHover({ row: data.length, col })}
                    onMouseLeave={() => setHover(null)}
                  >
                    <span
                      className="text-xs tabular-nums font-semibold"
                      style={{ color: getTextColor(val) }}
                    >
                      {fmt(val)}
                    </span>
                  </td>
                )
              })}
              <td style={{ borderTop: '2px solid #27272a' }} />
            </tr>
          </tbody>
        </table>
      </div>

      {/* ═══ MOBILE HEATMAP (<sm) ═══ */}
      <div className="sm:hidden">
        <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: '1px' }}>
          <thead>
            <tr>
              <th className="text-left text-zinc-400 text-[8px] font-medium w-8 pl-0.5" />
              {MONTH_SHORT.map((m, i) => (
                <th key={i} className="text-center text-zinc-400 text-[8px] font-medium py-1">
                  {m}
                </th>
              ))}
              <th
                className="text-center text-zinc-400 text-[8px] font-medium py-1 w-9"
                style={{ borderLeft: '2px solid #27272a' }}
              >
                Yr
              </th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, ri) => {
              const yr = yrMap.get(row.year)
              return (
                <tr key={row.year}>
                  <td className="text-zinc-400 text-[9px] tabular-nums font-medium pl-0.5">
                    {String(row.year).slice(2)}
                  </td>
                  {row.months.map((val, mi) => (
                    <td
                      key={mi}
                      className="text-center py-1.5 cursor-default"
                      style={{ backgroundColor: getCellColor(val) }}
                      onClick={() =>
                        setHover(
                          hover?.row === ri && hover?.col === mi + 1
                            ? null
                            : { row: ri, col: mi + 1 }
                        )
                      }
                    >
                      <span
                        className="text-[8px] tabular-nums font-semibold leading-none"
                        style={{ color: getTextColor(val) }}
                      >
                        {val !== null ? `${val >= 0 ? '+' : ''}${val.toFixed(0)}` : '–'}
                      </span>
                    </td>
                  ))}
                  <td
                    className="text-center py-1.5"
                    style={{ borderLeft: '2px solid #27272a' }}
                  >
                    {yr !== undefined ? (
                      <span
                        className={`text-[8px] tabular-nums font-bold ${
                          yr >= 0 ? 'text-green-500' : 'text-red-500'
                        }`}
                      >
                        {yr >= 0 ? '+' : ''}{yr.toFixed(0)}
                      </span>
                    ) : null}
                  </td>
                </tr>
              )
            })}
            {/* Mobile avg row */}
            <tr>
              <td
                className="text-zinc-400 text-[8px] font-medium uppercase pl-0.5"
                style={{ borderTop: '2px solid #27272a' }}
              >
                Av
              </td>
              {monthAvgs.map((val, mi) => (
                <td
                  key={mi}
                  className="text-center py-1.5 cursor-default"
                  style={{
                    backgroundColor: getCellColor(val),
                    borderTop: '2px solid #27272a',
                  }}
                  onClick={() =>
                    setHover(
                      hover?.row === data.length && hover?.col === mi + 1
                        ? null
                        : { row: data.length, col: mi + 1 }
                    )
                  }
                >
                  <span
                    className="text-[8px] tabular-nums font-semibold leading-none"
                    style={{ color: getTextColor(val) }}
                  >
                    {val !== null ? `${val >= 0 ? '+' : ''}${val.toFixed(0)}` : '–'}
                  </span>
                </td>
              ))}
              <td style={{ borderTop: '2px solid #27272a' }} />
            </tr>
          </tbody>
        </table>
      </div>

      {/* ═══ HOVER / TAP DETAIL ═══ */}
      <div className="mt-3 h-6">
        {hoverDetail && (
          <p className="text-zinc-400 text-xs">
            {hoverDetail.label}:{' '}
            <span
              className={`font-semibold ${
                hoverDetail.value >= 0 ? 'text-green-500' : 'text-red-500'
              }`}
            >
              {hoverDetail.value >= 0 ? '+' : ''}
              {hoverDetail.value.toFixed(2)}%
            </span>
          </p>
        )}
      </div>

      {/* ═══ LEGEND ═══ */}
      <div className="flex items-center gap-3 sm:gap-4 mt-1 flex-wrap">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5" style={{ backgroundColor: 'rgba(239,68,68,0.40)' }} />
          <span className="text-zinc-400 text-[10px]">&lt; -15%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5" style={{ backgroundColor: 'rgba(239,68,68,0.12)' }} />
          <span className="text-zinc-400 text-[10px]">0 to -15%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5" style={{ backgroundColor: 'rgba(34,197,94,0.12)' }} />
          <span className="text-zinc-400 text-[10px]">0 to +15%</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5" style={{ backgroundColor: 'rgba(34,197,94,0.40)' }} />
          <span className="text-zinc-400 text-[10px]">&gt; +15%</span>
        </div>
      </div>
    </div>
  )
}